import { h, Fragment } from "preact";
import { useEffect, useState } from "react";
import { Button, Input, Select, Radio, Typography } from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { initialServices } from "./initialServices";
import { ServiceCard } from "./ServiceCard";
import { CheckboxTree } from "./CheckboxTree";
import { VlinkModal } from "./VlinkModal";
import axios from "axios";
import { FileUpload } from "./FileUpload";
import FlowSettings from "./FlowSettings.jsx";

const { Option } = Select;

const FlowBuilder = () => {
    const languagePresets = {
        en: {
            url: "https://example.com/en",
            userTitle: "Welcome to the verification center",
            linkMask: "By checking the box next to this text, I sign",
        },
        ru: {
            url: "https://example.com/ru",
            userTitle: "Добро пожаловать в центр верификации",
            linkMask: "Ставя галочку возле этого текста я подписываю",
        },
        kk: {
            url: "https://example.com/kk",
            userTitle: "Тексеру орталығына қош келдіңіз",
            linkMask: "Осы мәтіннің жанына құсбелгі қойып мен қол қоямын",
        },
    };

    const initializeServices = (language, services) => {
        const presetValues = languagePresets[language];

        return services.map((service) => {
            if (service.paramName === "consent") {
                return {
                    ...service,
                    params: service.params.map((param) => ({
                        ...param,
                        value: presetValues?.[param.id] || param.value,
                    })),
                };
            }
            return service;
        });
    };

    const postData = {
        pipeline: [],
    };
    const [enabledServices, setEnabledServices] = useState([]);

    const [formData, setFormData] = useState({
        personId: '',
        IIN: '',
        fullName: '',
        flowLogo: '',
        phoneNumber: '',
        selectedLanguage: 'en'
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [vlink, setVlink] = useState("");
    const [error, setError] = useState("");
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [movingServiceId, setMovingServiceId] = useState(null);
    const [direction, setDirection] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [esignFileList, setEsignFileList] = useState([]);
    const [services, setServices] = useState(() =>
        initializeServices(formData.selectedLanguage, initialServices)
    );

    useEffect(() => {
        const inletService = enabledServices.find(
            (service) =>
                service.paramName === "device-filter" && service.type === "inlet"
        );

        if (inletService) {
            if (
                !services.some(
                    (service) =>
                        service.paramName === "device-filter" && service.type === "outlet"
                )
            ) {
                setServices((prevServices) => [
                    ...prevServices,
                    {
                        id: 10,
                        name: "Device Switch Outlet",
                        isActive: false,
                        paramName: "device-filter",
                        type: "outlet",
                        params: [],
                    },
                ]);
            }
        } else {
            setServices((prevServices) =>
                prevServices.filter(
                    (service) =>
                        !(
                            service.paramName === "device-filter" && service.type === "outlet"
                        )
                )
            );
        }
    }, [enabledServices]);

    const clearAll = () => {
        setEnabledServices([]);
        setServices(initialServices);
        setFormData(prevData => ({
            ...prevData,
            phoneNumber: "",
            IIN: "",
            fullName: "",
            personId: "",
        }));
        setFileList([]);
        setError("");
    };

    useEffect(() => {
        setServices(initializeServices(formData.selectedLanguage, services));
    }, [formData.selectedLanguage]);

    const toggleService = (id) => {
        setServices((prevServices) =>
            prevServices.map((service) =>
                service.id === id
                    ? { ...service, isActive: !service.isActive }
                    : service
            )
        );

        const service = services.find((service) => service.id === id);

        if (!service.isActive) {
            setEnabledServices((prevServices) => [...prevServices, service]);
        } else {
            setEnabledServices((prevServices) =>
                prevServices.filter((enabledService) => enabledService.id !== id)
            );
        }
        const newServices = services.map((service) =>
            service.id === id ? { ...service, isActive: !service.isActive } : service
        );
        setServices(newServices);
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;

        const sourceService = services[source.index];
        const destinationService = services[destination.index];

        if (
            destinationService.paramName === "consent" ||
            sourceService.paramName === "consent"
        ) {
            return;
        }

        const reorderedServices = Array.from(services);
        const [moved] = reorderedServices.splice(source.index, 1);
        reorderedServices.splice(destination.index, 0, moved);

        setServices(reorderedServices);
    };

    const getActiveServiceNumbers = (services) => {
        const activeServices = services.filter((service) => service.isActive);
        return activeServices.map((service, index) => ({
            id: service.id,
            number: index + 1,
        }));
    };

    const activeServiceNumbers = getActiveServiceNumbers(services);

    const moveService = (id, direction) => {
        const index = services.findIndex((service) => service.id === id);
        const newIndex = direction === "up" ? index - 1 : index + 1;

        if (newIndex < 0 || newIndex >= services.length) return;

        setMovingServiceId(id);
        setDirection(direction);

        setTimeout(() => {
            const newServices = Array.from(services);
            const [movedService] = newServices.splice(index, 1);
            newServices.splice(newIndex, 0, movedService);

            setServices(newServices);

            setTimeout(() => {
                setMovingServiceId(null);
                setDirection("");
            }, 750);
        }, 0);
    };

    const updateInputParam = (serviceId, paramId, value) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        return {
                            ...param,
                            value,
                        };
                    }
                    return param;
                });
                return {
                    ...service,
                    params: updatedParams,
                };
            }
            return service;
        });
        setServices(updatedServices);
    };

    const uploadFileData = async(file, uid, service) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.set("upload_file", file, file.name);
        formData.set("person_id", formData.personId);
        formData.set("label", "doc_preview");

        try {
            const res = await axios.post("/user/resources/storage", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            if(service === 'doc-preview'){
                setFileList((prev) => [...prev, {
                    name: file.name,
                    sessionId: res.data.session_id,
                    uid: uid,
                }]);
            }
            else if(service === 'esign-sign'){
                setEsignFileList((prev) => [...prev, {
                    name: file.name,
                    sessionId: res.data.session_id,
                    uid: uid,
                }])
            }
        } catch (err) {
            setError(err?.response?.data.error_msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileRemove = (uid, listType) => {
        if(listType === "doc-preview"){
            setFileList((prev) => prev.filter(item => item.uid !== uid));
        }
        else if(listType === "esign-sign"){
            setEsignFileList((prev) => prev.filter(item => item.uid !== uid));
        }
    };

    const updateCheckbox = (serviceId, paramId) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        return {
                            ...param,
                            enabled: !param.enabled,
                        };
                    }
                    return param;
                });
                return {
                    ...service,
                    params: updatedParams,
                };
            }
            return service;
        });
        setServices(updatedServices);
    };

    const updateSelectParam = (serviceId, paramId, value) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        return {
                            ...param,
                            value,
                        };
                    }
                    return param;
                });
                return {
                    ...service,
                    params: updatedParams,
                };
            }
            return service;
        });
        setServices(updatedServices);
    };

    const updateRadioGroup = (serviceId, paramId, selectedValue) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        const updatedOptions = param.options.map((option) => {
                            if (option.value === selectedValue) {
                                if (option.params) {
                                    const updatedChildParams = option.params.map(
                                        (childParam) => ({
                                            ...childParam,
                                            enabled: true,
                                        })
                                    );
                                    return {
                                        ...option,
                                        params: updatedChildParams,
                                        enabled: true,
                                    };
                                }
                                return {
                                    ...option,
                                    enabled: true,
                                };
                            }
                            return {
                                ...option,
                                enabled: false,
                            };
                        });

                        return {
                            ...param,
                            value: selectedValue,
                            options: updatedOptions,
                        };
                    }
                    return param;
                });

                return {
                    ...service,
                    params: updatedParams,
                };
            }
            return service;
        });

        setServices(updatedServices);
    };

    const updateChildCheckbox = (
        serviceId,
        paramId,
        optionValue,
        childParamId,
        isEnabled
    ) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        const updatedOptions = param.options.map((option) => {
                            if (option.value === optionValue && option.params) {
                                const updatedChildParams = option.params.map((childParam) => {
                                    if (childParam.id === childParamId) {
                                        return {
                                            ...childParam,
                                            enabled: isEnabled,
                                        };
                                    }
                                    return childParam;
                                });

                                return {
                                    ...option,
                                    params: updatedChildParams,
                                };
                            }
                            return option;
                        });

                        return {
                            ...param,
                            options: updatedOptions,
                        };
                    }
                    return param;
                });

                return {
                    ...service,
                    params: updatedParams,
                };
            }
            return service;
        });

        setServices(updatedServices);
    };

    const updateCheckboxNested = (serviceId, paramId, isEnabled) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        return {
                            ...param,
                            enabled: isEnabled,
                        };
                    }

                    if (param.params) {
                        return {
                            ...param,
                            params: updateChildParams(param.params, paramId, isEnabled),
                            enabled: param.params.some((child) => child.enabled),
                        };
                    }

                    return param;
                });

                return {
                    ...service,
                    params: updatedParams,
                };
            }

            if (paramId === "Include client card as a document" && service.paramName === "client-card") {
                return {
                    ...service,
                    isActive: isEnabled
                };
            }

            return service;
        });

        setServices(updatedServices);

        if (paramId === "Include client card as a document") {
            const clientCardService = services.find(service => service.paramName === "client-card");
            if (clientCardService) {
                if (isEnabled) {
                    setEnabledServices(prev => {
                        if (!prev.some(s => s.paramName === "client-card")) {
                            return [...prev, clientCardService];
                        }
                        return prev;
                    });
                } else {
                    setEnabledServices(prev =>
                        prev.filter(s => s.paramName !== "client-card")
                    );
                }
            }
        }
    };

    const updateChildParams = (childParams, paramId, isEnabled) => {
        return childParams.map((childParam) => {
            if (childParam.id === paramId) {
                return {
                    ...childParam,
                    enabled: isEnabled,
                };
            }

            if (childParam.params) {
                return {
                    ...childParam,
                    params: updateChildParams(childParam.params, paramId, isEnabled),
                    enabled: childParam.params.some((child) => child.enabled),
                };
            }

            return childParam;
        });
    };

    const updateChildSelectParams = (childParams, paramId, value) => {
        return childParams.map((childParam) => {
            if (childParam.id === paramId) {
                return {
                    ...childParam,
                    value,
                };
            }

            if (childParam.params) {
                return {
                    ...childParam,
                    params: updateChildSelectParams(childParam.params, paramId, value),
                };
            }

            return childParam;
        });
    };

    const updateSelectParamNested = (serviceId, paramId, value) => {
        const updatedServices = services.map((service) => {
            if (service.id === serviceId) {
                const updatedParams = service.params.map((param) => {
                    if (param.id === paramId) {
                        return {
                            ...param,
                            value,
                        };
                    }

                    if (param.params) {
                        return {
                            ...param,
                            params: updateChildSelectParams(param.params, paramId, value),
                        };
                    }

                    return param;
                });

                return {
                    ...service,
                    params: updatedParams,
                };
            }

            return service;
        });

        setServices(updatedServices);
    };

    const updateParamEnabled = (param, isEnabled) => {
        if (!param.params || param.params.length === 0) {
            return {
                ...param,
                enabled: isEnabled,
            };
        }

        const updatedChildParams = param.params.map((childParam) =>
            updateParamEnabled(childParam, isEnabled)
        );

        const hasEnabledChild = updatedChildParams.some(
            (childParam) => childParam.enabled
        );

        return {
            ...param,
            enabled: isEnabled || hasEnabledChild,
            params: updatedChildParams,
        };
    };

    const renderCardContent = (service) => {
        const serviceWithoutGeneral = service.params.filter(
            (item) =>
                item.paramName !== "allow_failure" &&
                item.paramName !== "timeout" &&
                item.paramName !== "max_retries"
        );
        const serviceWithGeneral = service.params.filter(
            (item) =>
                item.paramName === "allow_failure" ||
                item.paramName === "timeout" ||
                item.paramName === "max_retries"
        );

        const handleOpen = (serviceId) => {
            const updatedServices = services.map((service) => {
                if (service.id === serviceId) {
                    return {
                        ...service,
                        isOpen: !service.isOpen,
                    };
                }
                return service;
            });
            setServices(updatedServices);
        };

        const paramName = service.paramName;
        return (
            <div>
                {service.params &&
                    serviceWithoutGeneral.map((param, idx) => {
                        if (param.type === "input") {
                            return (
                                <Input
                                    placeholder={param.placeholder}
                                    value={param.value}
                                    onChange={(e) =>
                                        updateInputParam(service.id, param.id, e.target.value)
                                    }
                                    style={{ marginTop: 8 }}
                                />
                            );
                        } else if (param.type === "checkbox-select") {
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        paddingTop: "10px",
                                    }}
                                >
                                    <div>
                                        <input
                                            onChange={(e) => updateCheckbox(service.id, param.id)}
                                            type="checkbox"
                                            id={param.id}
                                            value={param.value}
                                            checked={param.enabled}
                                        />
                                        <label
                                            className="checkbox-select-label"
                                            htmlFor={param.id}
                                            style={{ marginLeft: "10px" }}
                                        >
                                            {param.id}
                                        </label>
                                    </div>
                                    <Select
                                        onChange={(value) =>
                                            updateSelectParam(service.id, param.id, value)
                                        }
                                        disabled={!param.enabled}
                                        value={param.value}
                                        style={{ width: "50%" }}
                                    >
                                        {param.options.map((option) => (
                                            <Option key={option} value={option} />
                                        ))}
                                    </Select>
                                </div>
                            );
                        } else if(param.type === "file-upload"){
                            return (
                                <div key={param.id} className="mt-4">
                                    <FileUpload
                                        onChange={(file, uid) => uploadFileData(file, uid, paramName)}
                                        onRemove={(uid) => handleFileRemove(uid, paramName)}
                                        personId={formData.personId}
                                    />
                                </div>
                            );
                        } else if (param.type === "radio-group") {
                            return (
                                <div style={{ paddingTop: "10px" }}>
                                    <Radio.Group
                                        value={param.value}
                                        onChange={(e) =>
                                            updateRadioGroup(service.id, param.id, e.target.value)
                                        }
                                    >
                                        {param.options.map((option) => (
                                            <Radio key={option.value} value={option.value}>
                                                {option.label}
                                            </Radio>
                                        ))}
                                    </Radio.Group>

                                    {param.options.map((option) => {
                                        if (option.value === param.value && option.params) {
                                            return (
                                                <div key={option.value} style={{ marginTop: "10px" }}>
                                                    {option.params.map((childParam) => (
                                                        <div key={childParam.id}>
                                                            <input
                                                                type="checkbox"
                                                                id={childParam.id}
                                                                checked={childParam.enabled}
                                                                onChange={(e) =>
                                                                    updateChildCheckbox(
                                                                        service.id,
                                                                        param.id,
                                                                        option.value,
                                                                        childParam.id,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            ></input>
                                                            <label
                                                                htmlFor={childParam.id}
                                                                style={{
                                                                    marginLeft: "10px",
                                                                    position: "static",
                                                                }}
                                                                className="checkbox-select-label"
                                                            >
                                                                {childParam.id}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            );
                        } else if (param.type === "checkbox") {
                            return (
                                <div>
                                    <CheckboxTree
                                        key={param.id}
                                        param={param}
                                        serviceId={service.id}
                                        updateCheckbox={updateCheckboxNested}
                                        updateSelectParam={updateSelectParamNested}
                                    />
                                </div>
                            );
                        }
                        return null;
                    })}
                {serviceWithGeneral.length > 0 && (
                    <Button
                        style={{ marginTop: "20px" }}
                        onClick={() => handleOpen(service.id)}
                    >
                        {service.isOpen ? "Hide general settings" : "Show general settings"}
                    </Button>
                )}
                {serviceWithGeneral.length > 0 && service.isOpen && (
                    <div>
                        <div>
                            {serviceWithGeneral.map((param, idx) => (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        paddingTop: "10px",
                                    }}
                                >
                                    <div>
                                        <input
                                            id={`${service.name}-${param.id}`}
                                            onChange={(e) => updateCheckbox(service.id, param.id)}
                                            type="checkbox"
                                            value={param.value}
                                            checked={param.enabled}
                                        />
                                        <label
                                            className="checkbox-select-label"
                                            htmlFor={`${service.name}-${param.id}`}
                                            style={{ marginLeft: "10px", position: "static" }}
                                        >
                                            {param.id}
                                        </label>
                                    </div>
                                    <Select
                                        onChange={(value) =>
                                            updateSelectParam(service.id, param.id, value)
                                        }
                                        disabled={!param.enabled}
                                        value={param.value}
                                        style={{ width: "50%" }}
                                    >
                                        {param.options.map((option) => (
                                            <Option key={option} value={option} />
                                        ))}
                                    </Select>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const collectConsentData = (service, lang, logoUrl) => {
        postData.pipeline.push({ service_name: "consent" });
        let consentText =
            service.params[0].value &&
            service.params[1].value &&
            service.params[2].value
                ? `${service.params[0].value} [${service.params[2].value}](${service.params[1].value})`
                : service.params[0].value;
        postData.ui = {
            logo_url: logoUrl,
            translation: {
                [lang]: {
                    ConsentAgreement1: consentText,
                },
            },
        };
    };

    const collectPhoneVerificationData = (service) => {
        let options = {};

        if (service.params.length > 0) {
            service.params.forEach((param) => {
                if (param.id === "allowFailure" && param.enabled) {
                    options.allow_failure = Boolean(param.value);
                }

                if (param.id === "timeout" && param.enabled) {
                    options.timeout = param.value;
                }

                if (param.id === "maxRetries" && param.enabled) {
                    options.max_retries = param.value;
                }

                if (param.id === "operatorSelection" && param.enabled) {
                    if (param.value === "all") {
                        options.operators = ["all"];
                    } else {
                        const customOption = param.options.find(
                            (option) => option.value === "custom" && option.enabled
                        );
                        if (customOption && customOption.params) {
                            const selectedOperators = customOption.params
                                .filter((operator) => operator.enabled)
                                .map((operator) => operator.value);

                            if (selectedOperators.length > 0) {
                                options.operators = selectedOperators;
                            }
                        }
                    }
                }
            });
        }

        postData.pipeline.push({ service_name: "veriphone", options });
    };

    const collectVeridocData = (service) => {
        let options = [];
        let config = {};

        let allow_failure = null;
        let timeout = null;
        let max_retries = null;

        service.params.forEach((param) => {
            if (
                param.enabled &&
                param.paramName !== "allow_failure" &&
                param.paramName !== "timeout" &&
                param.paramName !== "max_retries"
            ) {
                options.push(param.paramName);

                if (param.paramName === "scan") {
                    param.params.forEach((innerParam) => {
                        if (innerParam.enabled) {
                            if (innerParam.paramName === "mirror_preview") {
                                config.render = { [innerParam.paramName]: innerParam.enabled };
                                console.log(innerParam);
                            } else if (innerParam.paramName === "doc_type") {
                                let docTypeId;
                                if (innerParam.value === "Kazakhstan ID") {
                                    docTypeId = 1;
                                } else if (innerParam.value === "Kazakhstan residence permit") {
                                    docTypeId = 33;
                                } else if (innerParam.value === "MRZ passport") {
                                    docTypeId = 28;
                                } else if (innerParam.value === "Other passports") {
                                    docTypeId = 42;
                                } else {
                                    docTypeId = null;
                                }
                                innerParam.value = docTypeId;
                            } else if (innerParam.paramName === "recognition_mode") {
                                innerParam.value = innerParam.value === "One side" ? 0 : 1;
                            } else {
                                config[innerParam.paramName] = innerParam.value;
                            }
                        }
                    });
                }
            }
            if (param.paramName === "allow_failure") {
                allow_failure = param.value;
            } else if (param.paramName === "timeout") {
                timeout = param.value;
            } else if (param.paramName === "max_retries") {
                max_retries = param.value;
            }
        });

        postData.pipeline.push({
            service_name: "veridoc",
            options,
            config,
            ...(allow_failure !== null && { allow_failure }),
            ...(timeout !== null && { timeout }),
            ...(max_retries !== null && { max_retries }),
        });
    };

    const collectDeviceSwitchData = (service) => {
        service.params.length > 0
            ? postData.pipeline.push({
                service_name: "device-filter",
                qr_url: service.params[0].value,
                device_type: "mobile",
            })
            : postData.pipeline.push({
                service_name: "device-filter",
                device_type: "mobile",
            });
    };

    const collectDocPreviewData = (service) => {
        const files = [];
        const result = {
            service_name: service.paramName
        };

        if (service.params?.length > 0) {
            service.params.forEach((param) => {
                if (param.type === 'checkbox' && param.paramName !== 'includeClientCard') {
                    result[param.paramName] = Boolean(param.enabled);
                } else if (param.type === 'file-upload' && param.value) {
                    files.push(param.value);
                }

                if (param.paramName === 'includeClientCard' && param.enabled) {
                    files.push('client-card');
                }
            });
        }

        if (fileList.length > 0) {
            files.push("input_files");
        }

        if (files.length > 0) {
            result.files = files;
        }

        postData.pipeline.push(result);
    };

    const collectEsignData = (service) => {
        const esignAction = service.paramName;
        let options = {};
        if(esignAction === 'esign-issue'){
            postData.pipeline.push({
                service_name: "esign",
                action: "issue",
                skip_exists: true,
            });
        }
        else if(esignAction === 'esign-sign'){
            files = []
            if(esignFileList.length > 0){
                files.push('input_files');
            }
            if (service.params.length > 0) {
                service.params.forEach((param) => {
                    if (param.enabled && param.paramName === "includeClientCard") {
                        files.push('client-card');
                    }
                });
            }
            postData.pipeline.push({
                service_name: "esign",
                action: "sign",
                files: files,
                'forgot-password-option': true
            })
        }
    };

    const collectRestData = (service) => {
        let serviceObject = { service_name: service.paramName };

        if (service.params && service.params.length > 0) {
            service.params.forEach((param) => {
                if (param.enabled) {
                    serviceObject[param.paramName] = param.value;
                }
            });
        }

        postData.pipeline.push(serviceObject);
    };

    const collectData = () => {
        const activeServices = services.filter(
            (service) => service.isActive === true
        );

        activeServices.forEach((service) => {
            if (service.paramName === "consent") {
                collectConsentData(service, formData.selectedLanguage, formData.flowLogo);
            } else if (service.paramName === "veriphone") {
                collectPhoneVerificationData(service);
            } else if (service.paramName === "veridoc") {
                collectVeridocData(service);
            } else if (service.paramName === "device-filter") {
                collectDeviceSwitchData(service);
            } else if(service.paramName === "doc-preview"){
                collectDocPreviewData(service);
            } else if(service.paramName === "esign-issue" || service.paramName === "esign-sign") {
                collectEsignData(service);
            } else {
                collectRestData(service);
            }
        });
    };

    const prepareFlowData = () =>{
        return {
            callback_url: "https://verigram.ai",
            person_id: formData.personId,
            locale: formData.selectedLanguage,
            template: postData,
            input_data: {
                ...(formData.phoneNumber.length > 0 && { phone_number: formData.phoneNumber }),
                ...(formData.IIN.length > 0 && { iin: formData.IIN }),
                ...(formData.fullName.length > 0 && { full_name: formData.fullName }),
                ...((fileList.length > 0 || esignFileList.length > 0) && { files: {
                        doc_preview: fileList.map(file => ({
                            filename: file.name,
                            file_id: file.sessionId
                        })),
                        esign: esignFileList.map(esignFile => ({
                            filename: esignFile.name,
                            file_id: esignFile.sessionId
                        }))
                    }}),
            }};
    }
    const verifyData = async () => {
        setError("");
        setIsLoading(true);
        collectData();

        const data = prepareFlowData();

        await axios.post("/user/flow/verify", data).catch((err) => {
            setError(err?.response?.data.error_msg);
        });
        setIsLoading(false);
        return;
    };

    useEffect(() => {
        if (isFirstRender) {
            setIsFirstRender(false);
            return;
        }

        verifyData();
    }, [enabledServices, formData, fileList, esignFileList]);

    const sendRequest = async () => {
        setError("");
        collectData();

        const data = prepareFlowData();

        await axios
            .post("/user/flow", data)
            .then((res) => {
                setIsModalOpen(true);
                setVlink(res.data.vlink);
            })
            .catch((err) => {
                setError(err?.response?.data.error_msg);
            }).finally(()=>
                clearAll()
            );
    };

    return (
        <>
            <div style={{ display: "flex", columnGap: "0.5px" }}>
                <div className={`settings-tab selected`}>
                    <h4 className={`settings-tab-text selected`}>Create Flow</h4>
                </div>
            </div>
            <div
                style={{
                    display: "flex",
                    gap: "20px",
                    flexDirection: "column",
                    background: "white",
                }}
            >
                <div>
                    <div style={{ display: "flex", padding: "25px" }}>
                        <div style={{ display: "flex", gap: "20px" }}>
                            <div style={{ flex: 1 }}>
                                <h3>Create Flow</h3>
                                <div>
                                    <Typography.Text>
                                        <p>
                                            Create your own verification method, by combining Verigram
                                            services into one sequence - Flow. Simply switch on the
                                            modules you need and drag & drop them to place the
                                            services in desired order.
                                        </p>
                                    </Typography.Text>
                                </div>
                                <h3>Services</h3>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="services">
                                        {(provided) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                                {services.map((service, index) => {
                                                    const activeServiceNumber = activeServiceNumbers.find(
                                                        (num) => num.id === service.id
                                                    )?.number;

                                                    return (
                                                        <Draggable
                                                            key={service.id}
                                                            draggableId={String(service.id)}
                                                            index={index}
                                                            isDragDisabled={
                                                                service.paramName === "consent" ? true : false
                                                            }
                                                            isDropDisabled={
                                                                service.paramName === "consent" ? true : false
                                                            }
                                                        >
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                >
                                                                    <ServiceCard
                                                                        service={service}
                                                                        services={services}
                                                                        moveService={moveService}
                                                                        toggleService={toggleService}
                                                                        renderCardContent={renderCardContent}
                                                                        provided={provided}
                                                                        index={index}
                                                                        activeServiceNumber={activeServiceNumber}
                                                                        movingServiceId={movingServiceId}
                                                                        direction={direction}
                                                                    />
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                                {error && (
                                    <div style={{ paddingTop: "30px" }}>
                                        <span style={{ color: "red" }}>{error}</span>
                                    </div>
                                )}
                                <div style={{ paddingTop: "30px" }}>
                                    <Button
                                        disabled={enabledServices.length <= 0 || error?.length > 0 || isLoading}
                                        onClick={() => sendRequest()}
                                    >
                                        Create flow
                                    </Button>
                                    <Button
                                        style={{ marginLeft: "10px" }}
                                        onClick={() => clearAll()}
                                    >
                                        Clear all
                                    </Button>
                                </div>
                            </div>

                            <div className="flow-settings" style={{ flex: 1 }}>
                                <FlowSettings
                                    formData={formData}
                                    setFormData={setFormData}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {isModalOpen && (
                    <VlinkModal
                        vlink={vlink}
                        setIsModalOpen={setIsModalOpen}
                        isModalOpen={isModalOpen}
                        setVlink={setVlink}
                    />
                )}
            </div>
        </>
    );
};

export default FlowBuilder;
